import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import "dhtmlx-gantt/codebase/dhtmlxgantt.css";
import "dhtmlx-gantt";
import { useParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { fetchTasks, fetchTask, updateTask } from "@/store/taskSlice";
import MultiSelectDropdown from "@/components/MultiSelectDropdown";
import { userAPI } from "@/api/userAPI";
import { TaskModal } from "@/components/TaskModal/TaskModal";
import { commentAPI } from "@/api/commentAPI";
import { deleteComment, formatCommentsForActivity, updateComment } from "@/utils/commentUtils";
import { ensureHtml, toPlainText } from "@/utils/richText";
import { CommentActivity, Comment } from "@/types/comments";
import { Task } from "@/types";
import { sprintAPI } from "@/api/sprintAPI";
import toast from "react-hot-toast";
import { mastersAPI, WorkflowStatus, TaskType } from "@/api/mastersApi";
import SubtaskModal from "@/components/SubtaskModal/SubtaskModal";
import type { Subtask } from "@/types";
import { updateSubtask } from "@/store/taskSlice";
import { projectMemberAPI } from "@/api/projectMemberAPI";
import { taskAPI } from "@/api/taskAPI";
import { subtaskAPI } from "@/api/subtaskAPI";
import AssigneeChips from "@/components/AssigneeChips";
import { Search } from "lucide-react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import { TaskTypeIcon } from "@/components/TaskTypeIcon";
import { API_BASE_URL } from "@/utils/constants";

type Scale = "day" | "week" | "month" | "quarter";

const escapeHtml = (value: string) => {
  const str = String(value ?? "");
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const buildTimelineTooltip = (content: string, label: string) => {
  const safeLabel = escapeHtml(label || "Unassigned");
  return `
    <span class="timeline-avatar">
      ${content}
      <span class="timeline-avatar__tooltip">${safeLabel}</span>
    </span>
  `;
};

const resolveTaskTypeIconUrl = (raw?: string | null) => {
  if (!raw) return null;
  try {
    if (raw.startsWith("data:") || raw.startsWith("http://") || raw.startsWith("https://")) {
      return raw;
    }
    const api = new URL(API_BASE_URL);
    const origin = `${api.protocol}//${api.host}`;
    const path = raw.startsWith("/") ? raw : `/${raw}`;
    return origin + path;
  } catch {
    return null;
  }
};

const Timeline: React.FC = () => {
  const dispatch = useAppDispatch();
  const { projectId } = useParams<{ projectId: string }>();

  // Redux slices
  const { tasks: apiTasks, currentTask } = useAppSelector((state) => state.tasks);
  const { currentProject } = useAppSelector((state) => state.projects);

  // Local state
  const [tasks, setTasks] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [scale, setScale] = useState<Scale>("week");
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [usersById, setUsersById] = useState<Record<string, any>>({});
  const [sprints, setSprints] = useState<any[]>([]);
  const [sprintsById, setSprintsById] = useState<Record<string, any>>({});
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);

  // Task Modal States
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  // related work removed from modal usage
  const [activity, setActivity] = useState<CommentActivity[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);

  // Subtask Modal States
  const [activeSubtask, setActiveSubtask] = useState<Subtask | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState<Task | null>(null);

  const [selectedPersons, setSelectedPersons] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  
  // Dynamic height calculation for timeline
  const timelineContainerRef = useRef<HTMLDivElement | null>(null);
  const [timelineHeight, setTimelineHeight] = useState<number>(0);
  
  const recomputeTimelineHeight = () => {
    if (!timelineContainerRef.current) return;
    const rect = timelineContainerRef.current.getBoundingClientRect();
    const avail = window.innerHeight - rect.top - 16; // 16px bottom padding
    setTimelineHeight(Math.max(avail, 300));
  };

  useEffect(() => {
    // Wait next tick to ensure layout is ready
    setTimeout(recomputeTimelineHeight, 0);
    window.addEventListener('resize', recomputeTimelineHeight);
    window.addEventListener('orientationchange', recomputeTimelineHeight);
    return () => {
      window.removeEventListener('resize', recomputeTimelineHeight);
      window.removeEventListener('orientationchange', recomputeTimelineHeight);
    };
  }, []);
  type IconRootEntry = { root: Root; container: Element };
  const iconRootsRef = useRef<Record<string, IconRootEntry>>({});
  const ganttEventIdsRef = useRef<number[]>([]);
  const ganttInitializedRef = useRef(false);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchTasks({ project_id: projectId }));
    }
  }, [dispatch, projectId]);

  // Fetch sprints data
  useEffect(() => {
    const fetchSprints = async () => {
      if (projectId) {
        try {
          const sprintsData = await sprintAPI.getSprints(projectId);
          setSprints(sprintsData);

          // Create sprints lookup by ID
          const sprintsLookup: Record<string, any> = {};
          sprintsData.forEach(sprint => {
            sprintsLookup[sprint.id] = sprint;
          });
          setSprintsById(sprintsLookup);
        } catch (error) {
          console.error('Failed to fetch sprints:', error);
        }
      }
    };

    fetchSprints();
  }, [projectId]);

  // Resolve missing users by ID so avatars have names even if not in projectMembers
  useEffect(() => {
    let mounted = true;
    (async () => {
      const ids = Array.from(
        new Set(
          (apiTasks || [])
            .map((t: any) => t?.assigned_to)
            .filter((x: any) => x !== null && x !== undefined)
            .map(String)
        )
      );
      if (ids.length === 0) {
        if (mounted) setUsersById({});
        return;
      }
      // Build a quick lookup from projectMembers
      const pmLookup: Record<string, any> = {};
      projectMembers.forEach((m: any) => {
        const key = String(m.user_id ?? m._id ?? "");
        if (key) pmLookup[key] = m;
      });
      // Determine which IDs need fetching
      const toFetch = ids.filter((id) => !pmLookup[id] && !usersById[id]);
      if (toFetch.length === 0) return;
      try {
        const fetchedEntries = await Promise.all(
          toFetch.map(async (id) => {
            try {
              const u = await userAPI.getUser(id);
              return [id, u] as const;
            } catch (e) {
              // fallback minimal object
              return [id, { id, username: `User ${id}` }] as const;
            }
          })
        );
        const next: Record<string, any> = { ...usersById };
        fetchedEntries.forEach(([id, u]) => {
          next[id] = u;
        });
        if (mounted) setUsersById(next);
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [apiTasks, projectMembers]);

  // Load master statuses/types
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if(!projectId){
          throw new Error("No project Id is available")
      }
        const [workFlowStatuesData, types] = await Promise.all([
          mastersAPI.getWorkflowStatusesList(projectId),
          mastersAPI.getTaskTypes()
        ]);
        if (!mounted) return;
        setWorkflowStatuses(workFlowStatuesData.statuses);
        setTaskTypes(types);
      } catch {
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load project members when project changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!projectId) {
        if (mounted) setProjectMembers([]);
        return;
      }
      try {
        const data = await projectMemberAPI.getProjectMembers(projectId);
        if (mounted) setProjectMembers(data || []);
      } catch (error) {
        console.error('Failed to fetch project members:', error);
        if (mounted) setProjectMembers([]);
      }
    })();
    return () => { mounted = false; };
  }, [projectId]);

  // Task Modal Handlers
  const handleTaskClick = async (task: Task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);

    // Always refresh project members to ensure we have the latest list
    if (task.project_id) {
      try {
        const data = await projectMemberAPI.getProjectMembers(task.project_id);
        setProjectMembers(data || []);
      } catch (error) {
        console.error('Failed to refresh project members:', error);
      }
    }

    // Fetch dynamic data for the task using Redux and APIs
    try {
      await Promise.all([
        dispatch(fetchTask(task.id)),
        loadComments(task.id) // Load comments when task is clicked
      ]);

      // related work removed
    } catch (error) {
      console.error('Failed to fetch task details:', error);
    }
  };

  const handleTaskModalClose = () => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
    // related work removed
    setActivity([]);
    setComments([]);
  };

  const openSubtaskModal = (subtask: Subtask, parent: Task) => {
    setParentTaskForSubtask(parent);
    setIsTaskModalOpen(false);
    setActiveSubtask(subtask);
    setIsSubtaskModalOpen(true);
  };

  const closeSubtaskModal = () => {
    setIsSubtaskModalOpen(false);
    setActiveSubtask(null);
    setParentTaskForSubtask(null);
  };

  // Following BacklogView pattern - newStatus is already the workflow_status_id
  const handleStatusUpdate = async (taskId: string, workflowStatusId: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { workflow_status_id: workflowStatusId || undefined }
      })).unwrap();

      // Refresh tasks list
      if (currentProject?.id || selectedTask?.project_id) {
        dispatch(fetchTasks({ project_id: currentProject?.id || selectedTask?.project_id }));
        // Update selectedTask to reflect the status change
        if (selectedTask?.id === taskId) {
          dispatch(fetchTask(taskId));
        }
      } else {
        dispatch(fetchTasks({}));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  // Sync selectedTask with updated task from Redux state after status update (following BacklogView pattern)
  useEffect(() => {
    if (selectedTask && currentTask && currentTask.id === selectedTask.id) {
      setSelectedTask(currentTask);
    }
  }, [currentTask, selectedTask?.id]);

  const handleAddComment = async (taskId: string, html: string, parentId?: string, plainText?: string) => {
    try {
      const contentHtml = html;
      const contentText = plainText ?? toPlainText(ensureHtml(html || ""));
      const newComment = await commentAPI.createComment({
        task_id: taskId,
        content: contentText,
        content_html: contentHtml,
        content_text: contentText,
        parent_comment_id: parentId
      });

      // Reload comments to refresh the list
      await loadComments(taskId);

      return newComment;
    } catch (error) {
      console.error('Failed to add comment:', error);
      throw error; // Re-throw to let the modal handle the error
    }
  };

  // subtask handlers removed

  const handleUpdateDescription = async (taskId: string, description: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { description }
      })).unwrap();

      // Refresh the current task
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update description:', error);
      throw error;
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    await deleteComment(commentId);
    // Reload comments to refresh the list if a task is currently open
    if (selectedTask?.id) {
      await loadComments(selectedTask.id);
    }
  };

  const handleUpdateComment = async (commentId: string, html: string, plainText?: string) => {
    try {
      await updateComment(commentId, html, plainText);
      // Refresh activity if a task is currently open
      if (selectedTask?.id) {
        await loadComments(selectedTask.id);
      }
    } catch (error) {
      console.error('Failed to update comment:', error);
      throw error;
    }
  };

  const handleUpdateAssignee = async (taskId: string, assigneeId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { assigned_to: assigneeId === null ? null : assigneeId }
      })).unwrap();

      // Refresh tasks list and current task
      if (currentProject?.id) {
        dispatch(fetchTasks({ project_id: currentProject.id }));
      }
      await dispatch(fetchTask(taskId));

      // Refresh project members after assignee update to get latest list
      const projectIdForMembers = selectedTask?.project_id ?? currentProject?.id;
      if (projectIdForMembers) {
        try {
          const data = await projectMemberAPI.getProjectMembers(projectIdForMembers);
          setProjectMembers(data || []);
        } catch (error) {
          console.error('Failed to refresh project members:', error);
        }
      }
    } catch (error) {
      console.error('Failed to update assignee:', error);
      throw error;
    }
  };

  const handleUpdateReporter = async (taskId: string, reporterId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { reporter_id: reporterId === null ? null : reporterId }
      })).unwrap();

      // Refresh tasks list and current task
      if (currentProject?.id) {
        dispatch(fetchTasks({ project_id: currentProject.id }));
      }
      await dispatch(fetchTask(taskId));

      // Refresh project members after reporter update to get latest list
      const projectIdForMembers = selectedTask?.project_id ?? currentProject?.id;
      if (projectIdForMembers) {
        try {
          const data = await projectMemberAPI.getProjectMembers(projectIdForMembers);
          setProjectMembers(data || []);
        } catch (error) {
          console.error('Failed to refresh project members:', error);
        }
      }
    } catch (error) {
      console.error('Failed to update reporter:', error);
      throw error;
    }
  };

  const handleUpdateFields = async (taskId: string, updates: any) => {
    try {
      // Don't call updateTask again - it's already been called by the component
      // Just refresh the task data
      // Refresh tasks list and current task
      if (currentProject?.id) {
        dispatch(fetchTasks({ project_id: currentProject.id }));
      }
      await dispatch(fetchTask(taskId));

      // Also refresh from API to ensure we have latest data
      try {
        const fresh = await taskAPI.getTask(taskId);
        if (selectedTask?.id === taskId) {
          setSelectedTask(fresh);
        }
      } catch { /* ignore */ }

      // Refresh project members if assignee was updated
      if (updates.assigned_to !== undefined) {
        const projectIdForMembers = selectedTask?.project_id ?? currentProject?.id;
        if (projectIdForMembers) {
          try {
            const data = await projectMemberAPI.getProjectMembers(projectIdForMembers);
            setProjectMembers(data || []);
          } catch (error) {
            console.error('Failed to refresh project members:', error);
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh fields:', error);
      throw error;
    }
  };

  const handleUpdateTitle = async (id: string, newTitle: string) => {
    if (!currentTask || !selectedTask) return;

    try {
      await dispatch(updateTask({
        id: id || selectedTask.id,
        data: { title: newTitle }
      })).unwrap();
      const taskId = id || currentTask.id || selectedTask.id;
      // Refresh the current task
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update title:', error);
      toast.error('Failed to update title');
      throw error;
    }
  };

  // Update the loadComments function to use the utility:
  const loadComments = async (taskId: string) => {
    try {
      const commentsData = await commentAPI.getComments(taskId);
      setComments(commentsData);

      // Use the utility function to format comments
      const activityData = formatCommentsForActivity(commentsData);
      setActivity(activityData);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
      setActivity([]);
    }
  };

  const rawLabels: string | string[] | null | undefined = (currentTask as any)?.labels;


  // Prepare task details for the sidebar
  const getTaskDetails = () => {
    if (!currentTask) return {};

    return {
      assignee: currentTask.assigned_to || 'Unassigned',
      labels: Array.isArray(rawLabels)
        ? rawLabels
        : typeof rawLabels === "string"
          ? rawLabels.split(",").map((label: string) => label.trim())
          : [],

      dueDate: currentTask.due_date || 'Not set',
      parent: (currentTask as any).parent_id || 'None',
      team: 'Development Team',
      testTeam: 'QA Team',
      sme: [],
      sprint: currentTask.sprint_id || 'Not set',
      originalEstimate: (currentTask as any).estimate || 'Not set',
      timeRemaining: 'Not set',
      development: 'In Progress',
      reporter: 'Janarthanan',
      organization: 'Health Individual'
    };
  };

  const resolveTaskTypeMeta = useCallback(
    (task: any) => {
      if (!taskTypes || taskTypes.length === 0) return undefined;

      const candidates: string[] = [];
      const push = (value: any) => {
        if (value === null || value === undefined) return;
        const normalized = String(value).trim();
        if (!normalized) return;
        candidates.push(normalized.toLowerCase());
      };

      push(task.task_type_id);
      push((task as any)?.taskTypeId);
      push(task.task_type);
      push(task.task_type_name);
      push((task.task_type as any)?.id);
      push((task.task_type as any)?._id);
      push((task.task_type as any)?.name);
      push((task.task_type as any)?.key);
      push((task.task_type as any)?.slug);

      const uniqueCandidates = Array.from(new Set(candidates));

      return taskTypes.find((tt) => {
        const ttId = tt.id !== undefined ? String(tt.id).toLowerCase() : '';
        const ttKey = String((tt as any)?.key ?? '').toLowerCase();
        const ttSlug = String((tt as any)?.slug ?? '').toLowerCase();
        const ttName = String(tt.name ?? '').toLowerCase();
        return uniqueCandidates.some(
          (candidate) =>
            candidate &&
            (candidate === ttId || candidate === ttKey || candidate === ttSlug || candidate === ttName)
        );
      });
    },
    [taskTypes]
  );

  const mapTasksForGantt = (apiTasks: any[]) =>
    apiTasks.map((t) => {
      // Get sprint data for this task
      const sprint = t.sprint_id ? sprintsById[t.sprint_id] : null;
      // Use sprint dates if available, otherwise use task dates or defaults
      let startDate: Date;
      let endDate: Date;
      
      if (sprint && sprint.start_date && sprint.end_date) {
        startDate = new Date(sprint.start_date);
        endDate = new Date(sprint.end_date);
      } else if (t.created_at && t.due_date) {
        startDate = new Date(t.created_at);
        endDate = new Date(t.due_date);
      } else {
        // Default fallback
        startDate = new Date();
        endDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days from now
      }

      const typeMeta = resolveTaskTypeMeta(t);
      const iconRaw =
        typeMeta?.icon_url ||
        (typeMeta as any)?.icon ||
        (typeMeta as any)?.iconUrl ||
        t.task_type_icon ||
        t.task_type_icon_url ||
        t.taskTypeIcon ||
        (typeof t.task_type === "object" ? t.task_type?.icon_url || t.task_type?.icon : null);
      const iconUrl = resolveTaskTypeIconUrl(iconRaw);
      const typeName =
        typeMeta?.name ||
        t.task_type_name ||
        (typeof t.task_type === "object" ? t.task_type?.name : "") ||
        t.task_type ||
        "";

      // Normalize status: prefer explicit status, fallback to workflow status name, then default
      const statusName = (() => {
        const normalized = (s?: string) => String(s || '').toLowerCase();
        const byId = workflowStatuses.find(ws => ws.id === t?.workflow_status_id)?.name || '';
        return normalized(byId) || normalized(t?.status) || 'todo';
      })();

      return {
        id: t.id,
        text: t.title,
        person: t.assigned_to,
        type: t.task_type,
        status: statusName,
        start_date: startDate,
        end_date: endDate,
        progress: t.progress || 0,
        duration: Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)), // Calculate duration in days
        taskTypeIconUrl: iconUrl,
        taskTypeName: typeName,
      };
    });

  // Helper functions to get normalized task type and status names (matching ProjectList pattern)
  const getTaskTypeName = (t: any): string => {
    const normalized = (s?: string) => String(s || '').toLowerCase();
    const byId = taskTypes.find(tt => tt.id === t?.task_type_id)?.name || '';
    return normalized(t?.task_type) || normalized(byId);
  };


  const getTaskStatusName = (t: any): string => {
    const normalized = (s?: string) => String(s || '').toLowerCase();
    const byId = workflowStatuses.find(ws => ws.id === t?.workflow_status_id)?.name || '';
    if (byId) return normalized(byId);
    return normalized(t?.status);
  };

  const filterTasks = () => {
    // Scope API tasks to current projectId (server should do this, but enforce client-side as fallback)
    const scopedApiTasks = (apiTasks || []).filter((t: any) => {
      if (!projectId) return true;
      const pid = String(t.project_id ?? t.projectId ?? t.project ?? "");
      return pid === String(projectId);
    });

    // Filter tasks using normalized type and status names (matching ProjectList pattern)
    const normalize = (s?: string) => String(s || '').toLowerCase();
    const filteredApiTasks = scopedApiTasks.filter((t: any) => {
      // Person filter
      if (selectedPersons.length > 0 && !selectedPersons.includes(String(t.assigned_to))) return false;

      // Type filter: case-insensitive match with normalized names
      if (selectedTypes.length > 0) {
        const taskTypeName = getTaskTypeName(t);
        const match = selectedTypes.some((st: string) => normalize(st) === normalize(taskTypeName));
        if (!match) return false;
      }

      // Status filter: case-insensitive match with normalized names
      if (selectedStatuses.length > 0) {
        const taskStatusName = getTaskStatusName(t);
        const match = selectedStatuses.some((ss: string) => normalize(ss) === normalize(taskStatusName));
        if (!match) return false;
      }

      // Search filter
      if (search && !(t.title || '').toLowerCase().includes(search.toLowerCase())) return false;

      return true;
    });

    // Map filtered tasks to Gantt format
    const filtered = mapTasksForGantt(filteredApiTasks);
    setTasks(filtered);
  };

  useEffect(() => {
    filterTasks();
  }, [apiTasks, sprints, sprintsById, search, selectedPersons, selectedTypes, selectedStatuses, projectId, taskTypes, workflowStatuses]);

  const formatDisplay = (str: string) =>
    str
      ? str
        .split("_")
        .map((w) => w[0].toUpperCase() + w.slice(1))
        .join(" ")
      : "";

  const handleAssigneeToggle = (userId: string) => {
    setSelectedPersons((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  // Merge project members and fetched users into a unified person resolver
  const persons = React.useMemo(() => {
    const byId: Record<string, any> = {};
    projectMembers.forEach((m: any) => {
      const key = String(m.user_id ?? m._id ?? "");
      if (key) byId[key] = m;
    });
    Object.entries(usersById).forEach(([id, u]) => {
      if (!byId[id]) byId[id] = u;
    });
    return Object.entries(byId).map(([id, u]) => ({
      user_id: id,
      user_display_name: (u as any).user_display_name || (u as any).display_name || (u as any).full_name || (u as any).username || (u as any).email,
      user_email: (u as any).email,
      username: (u as any).username,
      _raw: u,
    }));
  }, [projectMembers, usersById]);

  const assigneeChipItems = useMemo(() => {
    const map = new Map<string, string>();
    persons.forEach((person: any) => {
      const id = String(person.user_id || "");
      if (!id) return;
      const name = person.user_display_name || person.username || person.user_email || "User";
      if (!map.has(id)) {
        map.set(id, name);
      }
    });

    if (map.size === 0) {
      (apiTasks || []).forEach((task: any) => {
        if (!task?.assigned_to) return;
        const id = String(task.assigned_to);
        if (!id || map.has(id)) return;
        const fallbackName =
          task.assignee_display_name ||
          task.assignee_name ||
          task.assignee ||
          task.assignee_email ||
          `User ${id}`;
        map.set(id, fallbackName);
      });
    }

    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [persons, apiTasks]);

  // Compute types and statuses from masters API with fallback to tasks (matching ProjectList pattern)
  const types = useMemo(() => {
    const fromMasters = taskTypes.map(tt => ({ value: String(tt.name).toLowerCase(), label: tt.name }));
    if (fromMasters.length > 0) return fromMasters;
    return Array.from(new Set((apiTasks || []).map((t: any) => String(t.task_type || '').toLowerCase()).filter(Boolean)))
      .map((t) => ({ value: t, label: formatDisplay(t) }));
  }, [taskTypes, apiTasks]);

  const statuses = useMemo(() => {
    const fromMasters = workflowStatuses.map(ws => ({ value: String(ws.name).toLowerCase(), label: ws.name }));
    if (fromMasters.length > 0) return fromMasters;
    return Array.from(new Set((apiTasks || []).map((t: any) => String(t.status || '').toLowerCase()).filter(Boolean)))
      .map((s) => ({ value: s, label: formatDisplay(s) }));
  }, [workflowStatuses, apiTasks]);

  const detachGanttEvents = useCallback(() => {
    const gantt = (window as any).gantt;
    if (!gantt) return;
    ganttEventIdsRef.current.forEach((id) => {
      gantt.detachEvent(id);
    });
    ganttEventIdsRef.current = [];
  }, []);

  const renderTaskTypeIcons = useCallback(() => {
    if (typeof document === "undefined") return;
    requestAnimationFrame(() => {
      const activeIds = new Set<string>();
      tasks.forEach((task) => {
        const taskId = String(task.id);
        activeIds.add(taskId);
        const container = document.querySelector<HTMLElement>(`.timeline-task-icon[data-task-id="${taskId}"]`);
        if (!container) {
          return;
        }
        const existing = iconRootsRef.current[taskId];
        if (!existing || existing.container !== container) {
          if (existing) {
            existing.root.unmount();
          }
          iconRootsRef.current[taskId] = {
            root: createRoot(container),
            container,
          };
        }

        iconRootsRef.current[taskId].root.render(
          <TaskTypeIcon
            url={task.taskTypeIconUrl || undefined}
            name={task.taskTypeName || task.text || "Task"}
            className="w-4 h-4 text-gray-600"
          />
        );
      });

      Object.keys(iconRootsRef.current).forEach((id) => {
        if (!activeIds.has(id)) {
          iconRootsRef.current[id].root.unmount();
          delete iconRootsRef.current[id];
        }
      });
    });
  }, [tasks]);

  useEffect(() => {
    const gantt = (window as any).gantt;
    if (!gantt) return;

    (window as any).handleTimelineTaskClick = (taskId: string) => {
      const task = (apiTasks || []).find((t) => {
        if (String(t.id) !== String(taskId)) return false;
        if (!projectId) return true;
        const pid = String(t.project_id);
        return pid === String(projectId);
      });
      if (task) {
        handleTaskClick(task);
      }
    };

    const statusColors: Record<string, string> = {
      done: "bg-green-200",
      in_progress: "bg-yellow-200",
      pending: "bg-gray-200",
      blocked: "bg-red-200",
      todo: "bg-blue-200",
    };

    gantt.config.columns = [
      {
        name: "combined",
        label: "",
        tree: true,
        width: 500,
        template: (task: any) => {
          const colorClass = statusColors[task.status] || "bg-gray-200";
          const statusBadge = `<span class="px-2 py-0.5 rounded text-xs ${colorClass}">${formatDisplay(task.status)}</span>`;

          const uid = String(task.person || '');
          const m = persons.find(
            (mm: any) =>
              String(mm.user_id) === String(uid) ||
              String((mm as any)._id) === String(uid)
          );
          const display = m?.user_display_name || m?.username || m?.user_email || '';

          let assigneeAvatar = '';
          if (!display || !task.person) {
            const unassignedContent = `
              <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200">
                <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </span>
            `;
            assigneeAvatar = buildTimelineTooltip(unassignedContent, "Unassigned");
          } else {
            const initials = (() => {
              const parts = display.trim().split(/\s+/);
              if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
              return (parts[0][0] + parts[1][0]).toUpperCase();
            })();

            const colorFromSeed = (seed: string) => {
              const colors = [
                'bg-blue-500',
                'bg-green-500',
                'bg-purple-500',
                'bg-orange-500',
                'bg-pink-500',
                'bg-indigo-500',
                'bg-teal-500',
                'bg-red-500',
                'bg-yellow-500',
                'bg-cyan-500',
              ];
              const sum = Array.from(seed).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
              return colors[sum % colors.length];
            };

            const color = colorFromSeed(uid || display || 'user');
            const assignedContent = `
              <span class="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-[10px] font-medium ${color}">
                ${initials}
              </span>
            `;
            assigneeAvatar = buildTimelineTooltip(assignedContent, display);
          }

          return `
            <div class="flex items-center justify-between w-full h-full px-2 gap-2">
              <div class="cursor-pointer hover:underline flex items-center" onclick="window.handleTimelineTaskClick('${task.id}')">
                <span class="timeline-task-icon" data-task-id="${task.id}"></span>
                <span class="ml-2">${task.text}</span>
              </div>
              <div class="flex items-center gap-3">
                ${statusBadge}
                ${assigneeAvatar}
              </div>
            </div>
          `;
        },
      },
    ];

    if (!ganttInitializedRef.current) {
      gantt.config.readonly = true;
      gantt.config.drag_resize = false;
      gantt.config.drag_move = false;
      gantt.config.drag_progress = false;
      gantt.config.show_links = false;
      gantt.config.show_grid = true;
      gantt.config.show_task_cells = true;
      gantt.config.scroll_size = 20;
      gantt.config.row_height = 40;
      gantt.config.bar_height = 20;
      gantt.config.grid_width = 500;
      gantt.config.autosize = false;
      gantt.config.scroll_size = 10;

      gantt.templates.task_class = function (_start: any, _end: any, task: any): string {
        const classColors: Record<string, string> = {
          done: "gantt_task_done",
          completed: "gantt_task_completed",
          in_progress: "gantt_task_in_progress",
          pending: "gantt_task_pending",
          blocked: "gantt_task_blocked",
          todo: "gantt_task_todo",
        };
        return classColors[task.status as string] || "gantt_task_default";
      };

      const container = document.getElementById("gantt_here");
      if (!container) {
        console.warn('Gantt container not found, skipping initialization');
        return;
      }

      try {
        gantt.init("gantt_here");
        ganttInitializedRef.current = true;
      } catch (error) {
        console.error('Gantt initialization error:', error);
        return;
      }
    }

    if (!ganttInitializedRef.current) {
      return;
    }

    detachGanttEvents();
    const eventIds = [
      gantt.attachEvent("onDataRender", () => {
        renderTaskTypeIcons();
        return true;
      }),
      gantt.attachEvent("onGanttRender", () => {
        renderTaskTypeIcons();
        return true;
      }),
      gantt.attachEvent("onAfterTaskAdd", () => {
        renderTaskTypeIcons();
        return true;
      }),
      gantt.attachEvent("onAfterTaskUpdate", () => {
        renderTaskTypeIcons();
        return true;
      }),
      gantt.attachEvent("onAfterTaskDelete", () => {
        renderTaskTypeIcons();
        return true;
      }),
      gantt.attachEvent("onGanttScroll", () => {
        renderTaskTypeIcons();
        return true;
      }),
    ];
    ganttEventIdsRef.current = eventIds;

    gantt.clearAll();
    gantt.parse({ data: tasks });
    setGanttScale(scale);
    gantt.showDate(new Date());
    renderTaskTypeIcons();

    return () => {
      detachGanttEvents();
    };
  }, [tasks, persons, scale, projectId, apiTasks, renderTaskTypeIcons, detachGanttEvents]);

  useEffect(() => {
    return () => {
      Object.keys(iconRootsRef.current).forEach((id) => {
        iconRootsRef.current[id].root.unmount();
        delete iconRootsRef.current[id];
      });
      detachGanttEvents();
    };
  }, [detachGanttEvents]);

  const setGanttScale = (scale: Scale) => {
    const gantt = (window as any).gantt;
    if (!gantt) return;
    const startDate = new Date();
    startDate.setFullYear(startDate.getFullYear() - 1); // previous year
    gantt.config.start_date = startDate;

    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 2); // next two years
    gantt.config.end_date = endDate;
    switch (scale) {
      case "day":
        gantt.config.scale_unit = "day";
        gantt.config.date_scale = "%d %M";
        gantt.config.subscales = [];
        break;
      case "week":
        gantt.config.scale_unit = "week";
        gantt.config.date_scale = "Week #%W";
        gantt.config.subscales = [{ unit: "day", step: 1, date: "%d %M" }];
        break;
      case "month":
        gantt.config.scale_unit = "month";
        gantt.config.date_scale = "%F %Y";
        gantt.config.subscales = [{ unit: "week", step: 1, date: "Week #%W" }];
        break;
      case "quarter":
        gantt.config.scale_unit = "quarter";
        gantt.config.date_scale = "Q%q %Y";
        gantt.config.subscales = [{ unit: "month", step: 1, date: "%M" }];
        break;
    }
    gantt.render();
    gantt.showDate(new Date());
  };

  // REMOVED THE LOADING CHECK THAT WAS CAUSING THE TIMELINE TO DISAPPEAR
  // if (bootLoading || loading) {
  //   return (
  //     <Loader variant="overlay" size="xl" text="Loading timeline..." />
  //   );
  // }

  return (
    <div className="p-4 space-y-4" style={{ height: '100vh', overflow: 'hidden' }}>
      {/* Add custom CSS for timeline bars */}
      <style>{`
  .gantt_task_done { background-color: #10b981 !important; }
  .gantt_task_completed { background-color: #10b981 !important; }
  .gantt_task_in_progress { background-color: #3b82f6 !important; }
  .gantt_task_pending { background-color: #6b7280 !important; }
  .gantt_task_blocked { background-color: #ef4444 !important; }
  .gantt_task_todo { background-color: #8b5cf6 !important; }
  .gantt_task_default { background-color: #8b5cf6 !important; }

  .gantt_task_line {
    border-radius: 4px !important;
    height: 20px !important;
  }

  .gantt_task_progress {
    background-color: rgba(255, 255, 255, 0.3) !important;
    border-radius: 4px !important;
  }

  .gantt_grid_scale .gantt_grid_head_cell {
    background-color: #f9fafb !important;
    border-right: 1px solid #e5e7eb !important;
    font-weight: 600 !important;
  }

  .gantt_grid_data .gantt_row {
    border-bottom: 1px solid #e5e7eb !important;
  }

  .gantt_grid_data .gantt_cell {
    border-right: 1px solid #e5e7eb !important;
  }

  .timeline-avatar {
    position: relative;
    display: inline-flex;
          overflow: visible;
  }

  .timeline-task-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
  }

  .timeline-avatar__tooltip {
    position: absolute;
          top: calc(100% + 4px);
          left: 50%;
          transform: translateX(-50%);
    background-color: #111827;
    color: #fff;
    font-size: 11px;
    font-weight: 500;
          padding: 2px 8px;
    border-radius: 6px;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    white-space: nowrap;
          pointer-events: none;
    opacity: 0;
          transition: opacity 150ms ease-out;
          z-index: 99999 !important;
  }

        /* Ensure Gantt container doesn't clip tooltips */
        .gantt_container {
    overflow: visible !important;
  }

        .gantt_grid_data .gantt_row {
    overflow: visible !important;
  }

  .gantt_grid_data .gantt_cell {
          overflow: visible !important;
        }

        .timeline-avatar:hover .timeline-avatar__tooltip,
        .timeline-avatar:focus-within .timeline-avatar__tooltip {
          opacity: 1;
        }
`}</style>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 md:w-[80%]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search timeline"
            className="w-64 h-8 pl-10 pr-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
        <AssigneeChips
          items={assigneeChipItems}
          selectedIds={selectedPersons}
          onToggle={handleAssigneeToggle}
        />
        <MultiSelectDropdown
          title="Type"
          options={types.map((t) => t.value)}
          labels={types.reduce((acc, t) => ({ ...acc, [t.value]: t.label }), {})}
          selected={selectedTypes}
          setSelected={setSelectedTypes}
        />
        <MultiSelectDropdown
          title="Status"
          options={statuses.map((s) => s.value)}
          labels={statuses.reduce((acc, s) => ({ ...acc, [s.value]: s.label }), {})}
          selected={selectedStatuses}
          setSelected={setSelectedStatuses}
        />
      </div>

      {/* Timeline */}
      <div ref={timelineContainerRef} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Let Gantt render and handle its own horizontal scrolling */}
        <div id="gantt_here" style={{ width: "100%", height: timelineHeight ? `${timelineHeight}px` : undefined }} />
      </div>
      {/* Scale Buttons */}
      <div className="flex gap-2 mt-2 justify-end">
        {["day", "week", "month", "quarter"].map((s) => (
          <button
            key={s}
            onClick={() => setScale(s as Scale)}
            className={`px-3 py-1 rounded font-semibold ${scale === s
                ? "bg-blue-100 border border-blue-500 text-blue-500"
                : "bg-gray-100"
              }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}

      </div>

      {/* Task Detail Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={handleTaskModalClose}
        task={currentTask || selectedTask}
        onStatusUpdate={handleStatusUpdate}
        onAddComment={handleAddComment}
        onUpdateDescription={handleUpdateDescription}
        onUpdateAssignee={handleUpdateAssignee}
        onUpdateReporter={handleUpdateReporter}
        onUpdateFields={handleUpdateFields}
        activity={activity}
        taskDetails={getTaskDetails()}
        projectMembers={persons}
        comments={comments}
        onDeleteComment={handleDeleteComment}
        onUpdateComment={handleUpdateComment}
        projectKey={currentProject?.key || 'AHM'}
        onUpdateTaskTitle={handleUpdateTitle}
        onOpenSubtask={openSubtaskModal}
        workflowStatuses={workflowStatuses}
        project={currentProject}
      />

      {/* Subtask Modal */}
      {activeSubtask && (
        <SubtaskModal
          subtask={activeSubtask}
          isOpen={isSubtaskModalOpen}
          onClose={closeSubtaskModal}
          parentKey={`${currentProject?.key || 'AHM'}-${(parentTaskForSubtask as any)?.display_number ?? (currentTask as any)?.display_number ?? (selectedTask as any)?.display_number ?? ''}`.replace(/-$/, '')}
          parentTitle={(parentTaskForSubtask || currentTask || selectedTask)?.title}
          parentTask={(parentTaskForSubtask || currentTask || selectedTask) as any}
          onOpenParent={(_parent) => {
            closeSubtaskModal();
            if (_parent) {
              handleTaskClick(_parent);
            }
          }}
          projectMembers={persons}
          activity={activity}
          projectKey={currentProject?.key || 'AHM'}
          project={currentProject}
          onAddComment={handleAddComment}
          onUpdateComment={handleUpdateComment}
          onDeleteComment={handleDeleteComment}
          onSave={async (updates) => {
            await dispatch(updateSubtask({ subtaskId: activeSubtask.id, data: updates })).unwrap();
            // Refresh subtask data
            try {
              const fresh = await subtaskAPI.get(activeSubtask.id);
              setActiveSubtask(fresh);
            } catch (error) {
              console.error('Failed to refresh subtask:', error);
            }
            // Refresh project members after subtask update
            if (currentProject?.id) {
              try {
                const data = await projectMemberAPI.getProjectMembers(currentProject.id);
                setProjectMembers(data || []);
              } catch (error) {
                console.error('Failed to refresh project members:', error);
              }
            }
          }}
          onDelete={async () => {
            setIsSubtaskModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

// related work helper removed

export default Timeline;